import { defineComponent as _defineComponent } from 'vue'
import { computed, toRefs, watch } from 'vue';
  import { useIssueTemplate } from '@/ui/concerns/issue/useIssueTemplate';
  import { InputField, CheckboxField, SwitchField } from '@/ui/components/form';
  import { Field } from '@/ui/components/form/Field';
  import PrioritySelector from '@/_legacy/components/Issues/PrioritySelector.vue';
  import { DueAfterSelection } from '@/ui/components/issueTemplate/issueCreateTemplateModal/DueAfterSelection';
  import Approvals from '@/_legacy/views/ChecklistBuilder/Settings/Approvals.vue';
  import AssigneesDropdown from '@/ui/pages/user/assignees/AssigneesDropdown.vue';
  import { CreateUserModelIdentifier } from '@/ui/components/user/CreateUserModal';
  import type { IssueTemplateBuilderEditable } from '@/ui/DTO/issueTemplateBuilderEditable';
  import type { SelectOptions } from '@/ui/types';
  import type { DueAfter } from '@/entities/issueTemplate';
  import { IssuePriority } from '@/entities/issue';
  import type { OptVal } from '@/ui/concerns/issue/useIssueTemplate';

  type ApprovalsUsersPayload<V extends string | number = string> = { users: V[] };

  
export default /*#__PURE__*/_defineComponent({
  __name: 'IssueTemplateBuilder',
  props: {
    record: null,
    assigneeOptions: null,
    assigneeApprovalOptions: null
  },
  setup(__props: any) {

const props = __props as {
    record: IssueTemplateBuilderEditable;
    assigneeOptions: SelectOptions;
    assigneeApprovalOptions: SelectOptions;
  };

  

  const {
    assigneeOptions,
    record,
  } = toRefs(props);
  const {
    validationErrors,
    showEntireDueAfterBox,
    toggleDueAfterBox,
    canSeeApprovals,
    isShowFormSubmitterActionCreated,
    normalizeAssignees,
  } = useIssueTemplate(record);

  watch(
    assigneeOptions,
    opts => {
      if (opts && opts.length > 0) {
        removeDeletedAssignees(opts);
      }
    },
    { immediate: true },
  );

  const assigneeValues = computed(() => [
    ...record.value.assignees.value,
    ...record.value.associateAssignees.value,
    ...record.value.assignedGroups.value,
  ]);

  function removeDeletedAssignees(options: { value: OptVal }[]) {
    const allowed = new Set<string>(options.map(o => String(o.value)));

    const keep = (arr: string[]) => arr.filter(v => allowed.has(String(v)));

    record.value.assignees.value = keep(record.value.assignees.value);
    record.value.assignedGroups.value = keep(record.value.assignedGroups.value);
  }

  function onTitleDefinedByInspectorChange(v: boolean) {
    record.value.title.definedByInspector = v;
    if (v) {
      record.value.title.value = null;
      record.value.title.required = true;
    } else {
      record.value.title.required = false;
    }
  }

  function onTitleRequiredChange(_value: boolean) { /* disabled; no-op to satisfy API */ }
  function onTitleInput(v: string) {
    record.value.title.value = v;
  }

  function onDescriptionDefinedByInspectorChange(v: boolean) {
    record.value.description.definedByInspector = v;
    if (v) {
      record.value.description.value = null;
    } else {
      record.value.description.required = false;
    }
  }

  function onDescriptionRequiredChange(v: boolean) {
    record.value.description.required = v;
  }

  function onDescriptionInput(v: string) {
    record.value.description.value = v;
  }

  function onAssigneesDefinedByInspectorChange(v: boolean) {
    record.value.assignees.definedByInspector = v;
    if (v) {
      record.value.assignees.value = [];
    } else {
      record.value.assignees.required = false;
    }
  }

  function onAssigneesRequiredChange(v: boolean) {
    record.value.assignees.required = v;
  }

  function onAssigneesDropdownInput(value: (string | number)[]) {
    normalizeAssignees(value);
  }

  function onEntityItemSpecificAssignmentsChange(v: boolean) {
    record.value.entityItemSpecificAssignments = v;
  }

  function onPriorityDefinedByInspectorChange(v: boolean) {
    record.value.priority.definedByInspector = v;
    if (v) {
      record.value.priority.value = IssuePriority.Normal;
    } else {
      record.value.priority.required = false;
    }
  }

  function onPriorityRequiredChange(v: boolean) {
    record.value.priority.required = v;
  }

  function onPriorityInput(value: number) {
    record.value.priority.value = value;
  }

  function onDueAfterToggle(v: boolean) {
    toggleDueAfterBox(v);
  }

  function onDueInDefinedByInspectorChange(v: boolean) {
    record.value.dueIn.definedByInspector = v;
    if (!v) {
      record.value.dueIn.value = null;
    } else {
      record.value.dueIn.required = false;
    }
  }

  function onDueInRequiredChange(v: boolean) {
    record.value.dueIn.required = v;
  }

  function onDueAfterSelectionInput(value: DueAfter) {
    record.value.dueIn.value = value;
  }

  function onPhotosDefinedByInspectorChange(v: boolean) {
    record.value.photos.definedByInspector = v;
    if (!v) {
      record.value.photos.required = false;
    }
  }

  function onPhotosRequiredChange(v: boolean) {
    record.value.photos.required = v;
  }

  function onApprovalsEnabledUpdate(v: boolean) {
    record.value.approvalsEnabled = v;
  }

  function onEntityRestrictionsUpdate(v: boolean) {
    record.value.approvalsEntitySpecificAssignments = v;
  }

  function onChecklistApproversUpdate(v: ApprovalsUsersPayload) {
    record.value.approvers.value = v.users;
  }

  function onShowIssueHasBeenCreatedChange(v: boolean) {
    record.value.showIssueHasBeenCreated = v;
  }

  const r = record.value;
  const createUserIdentifier = CreateUserModelIdentifier.issueBuilderApprovers;

return { __sfc: true,props, assigneeOptions, record, validationErrors, showEntireDueAfterBox, toggleDueAfterBox, canSeeApprovals, isShowFormSubmitterActionCreated, normalizeAssignees, assigneeValues, removeDeletedAssignees, onTitleDefinedByInspectorChange, onTitleRequiredChange, onTitleInput, onDescriptionDefinedByInspectorChange, onDescriptionRequiredChange, onDescriptionInput, onAssigneesDefinedByInspectorChange, onAssigneesRequiredChange, onAssigneesDropdownInput, onEntityItemSpecificAssignmentsChange, onPriorityDefinedByInspectorChange, onPriorityRequiredChange, onPriorityInput, onDueAfterToggle, onDueInDefinedByInspectorChange, onDueInRequiredChange, onDueAfterSelectionInput, onPhotosDefinedByInspectorChange, onPhotosRequiredChange, onApprovalsEnabledUpdate, onEntityRestrictionsUpdate, onChecklistApproversUpdate, onShowIssueHasBeenCreatedChange, r, createUserIdentifier, InputField, CheckboxField, SwitchField, Field, PrioritySelector, DueAfterSelection, Approvals, AssigneesDropdown }
}

})